import { useEffect, type RefObject } from 'react';

/**
 * Tell the map when its container changes size (inspector collapse, window
 * resize) so it repaints the newly exposed area instead of leaving a blank
 * strip. Current Maps JS versions also observe this themselves; the explicit
 * event is a cheap guarantee for versions that do not.
 */
export const useMapContainerResize = (
  isLoaded: boolean,
  containerRef: RefObject<HTMLElement | null>,
  mapRef: RefObject<unknown>
) => {
  useEffect(() => {
    const container = containerRef.current;
    if (!isLoaded || !container || typeof ResizeObserver === 'undefined') {
      return;
    }
    let last: { width: number; height: number } | null = null;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[entries.length - 1]?.contentRect;
      if (!rect) return;
      const size = { width: rect.width, height: rect.height };
      const changed =
        last !== null &&
        (last.width !== size.width || last.height !== size.height);
      last = size;
      if (!changed || !mapRef.current) return;
      window.google?.maps?.event?.trigger(mapRef.current, 'resize');
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [isLoaded, containerRef, mapRef]);
};
